import { token } from '@atlaskit/tokens';
import {
  VERT_DRAG_HANDLER_ICON_WIDTH,
  DRAG_HANDLER_TD_ELEMENT_WIDTH,
} from '@databases/icons';
import Z_INDEX from '@databases/layout/z-index';
import { isNativeEmbedEditorPlugin } from '@databases/modes';
import { encodeCssUrl } from '@databases/utils/utils';
import svgToMiniDataURI from 'mini-svg-data-uri';
import React, { type FunctionComponent, useMemo } from 'react';

import { isFeatureFlagEnabled } from '@canvas/client-feature-flags';

import { getSkeletonRowSvg } from '../../../lazy-render';

import { StyledEntryPrefixTd } from './EntryPrefix';

interface EntrySkeletonProps {
  hasNumberedEntries: boolean;
  index: number;
  fieldCount: number;
  row: { type: 'gap'; height: number };
  columnWidths: number[];
}

const EntrySkeleton: FunctionComponent<EntrySkeletonProps> = ({
  hasNumberedEntries,
  index,
  fieldCount,
  row,
  columnWidths,
}) => {
  const skeletonSvg = useMemo(
    () => svgToMiniDataURI(getSkeletonRowSvg(columnWidths.slice(1))),
    [columnWidths]
  );

  const isVisualRefreshM1Enabled = isFeatureFlagEnabled(
    'databases-frontend-visual-refresh-table-m1'
  );
  const dragHandlerElementWidth = isVisualRefreshM1Enabled
    ? DRAG_HANDLER_TD_ELEMENT_WIDTH
    : VERT_DRAG_HANDLER_ICON_WIDTH;

  return (
    <tr
      style={{
        background: 'transparent',
        // eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
        left: isNativeEmbedEditorPlugin() ? 0 : dragHandlerElementWidth / 2,
      }}
    >
      {/* Entry selector gap */}
      {!isNativeEmbedEditorPlugin() && (
        <div
          style={{
            position: 'sticky',
            zIndex: Z_INDEX.ROW_SELECT_BUTTON,
            width: dragHandlerElementWidth / 2,
            border: 'none',
            left: 0,
            background: token('color.background.input.pressed', '#FFFFFF'),
          }}
        />
      )}
      {hasNumberedEntries && (
        <StyledEntryPrefixTd
          className="entry-number-table-data"
          hasNumberedEntries={hasNumberedEntries}
        >
          {index + 1}
        </StyledEntryPrefixTd>
      )}
      <td
        colSpan={fieldCount}
        style={{
          gridColumn: hasNumberedEntries ? '3 / -1' : '2 / -1',
          height: row.height,
          background: `url(${encodeCssUrl(skeletonSvg)}) repeat-y`,
          borderLeft: 'none',
        }}
      />
    </tr>
  );
};

export default EntrySkeleton;
